import { holdsAnywhere } from '@carii/casl-abilities';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router';
import { Button } from '../../components/ui/Button.js';
import { Card } from '../../components/ui/Card.js';
import { FormField, Input, Select } from '../../components/ui/FormField.js';
import { PageHeader } from '../../components/ui/PageHeader.js';
import { Spinner } from '../../components/ui/Spinner.js';
import { useAbility } from '../../context/AbilityContext.js';
import { api } from '../../services/api.js';
import {
  KIND_WORD,
  PROVIDER_WORD,
  type PaymentList,
  type RefundList,
  STATUS_WORD,
  paymentsToCsv,
  refundsToCsv,
  shortRef,
} from './financeCsv.js';

/**
 * 351 (#948): payments and refunds across the platform, for the people who handle money. Read-only: refunds are still
 * logged and approved on the booking page. Each tab needs its own PLATFORM-WIDE permission (a supplier's staff hold
 * `read Payment` for their own organisation only). Every figure is the server's; the CSV is the listed rows.
 */
type Tab = 'payments' | 'refunds';
const MYT_OFFSET_MS = 8 * 60 * 60 * 1000;
const mytToday = () => new Date(Date.now() + MYT_OFFSET_MS).toISOString().slice(0, 10);
const rm = (minor: number) =>
  `RM ${(minor / 100).toLocaleString('en-MY', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const when = (iso: string | null) =>
  iso
    ? new Date(iso).toLocaleString('en-GB', {
        timeZone: 'Asia/Kuala_Lumpur',
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      })
    : '—';
const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`;

function download(name: string, text: string) {
  const url = URL.createObjectURL(new Blob([text], { type: 'text/csv;charset=utf-8' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  a.click();
  URL.revokeObjectURL(url);
}

const TH = 'px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-steel';
const TD = 'px-3 py-2 text-sm text-ink align-top';

export function FinancePage() {
  const ability = useAbility();
  const seesPayments = holdsAnywhere(ability as never, 'read', 'Payment');
  const seesRefunds = holdsAnywhere(ability as never, 'read', 'Refund');
  const [tab, setTab] = useState<Tab>(seesPayments ? 'payments' : 'refunds');
  const [from, setFrom] = useState(() => `${mytToday().slice(0, 7)}-01`);
  const [to, setTo] = useState(() => mytToday());
  const [status, setStatus] = useState('');
  const [provider, setProvider] = useState('');
  const [q, setQ] = useState('');

  const params = new URLSearchParams({ from, to });
  if (status) params.set('status', status);
  if (tab === 'payments' && provider) params.set('provider', provider);
  if (q.trim()) params.set('q', q.trim());
  const url = `/finance/${tab}?${params.toString()}`;
  const list = useQuery({
    queryKey: ['finance', url],
    queryFn: () => api.get(url) as Promise<PaymentList | RefundList>,
    enabled: (tab === 'payments' ? seesPayments : seesRefunds) && Boolean(from && to),
  });

  const switchTab = (t: Tab) => {
    setTab(t);
    setStatus('');
  };

  const payments = tab === 'payments' ? (list.data as PaymentList | undefined) : undefined;
  const refunds = tab === 'refunds' ? (list.data as RefundList | undefined) : undefined;

  return (
    <div className="flex flex-col gap-4">
      <PageHeader
        title="Finance"
        description="Payments and refunds across the platform, in Malaysia time."
      />
      <div role="tablist" aria-label="Finance lists" className="flex gap-2">
        {seesPayments && (
          <Button
            variant={tab === 'payments' ? 'primary' : 'secondary'}
            role="tab"
            aria-selected={tab === 'payments'}
            onClick={() => switchTab('payments')}
          >
            Payments
          </Button>
        )}
        {seesRefunds && (
          <Button
            variant={tab === 'refunds' ? 'primary' : 'secondary'}
            role="tab"
            aria-selected={tab === 'refunds'}
            onClick={() => switchTab('refunds')}
          >
            Refunds
          </Button>
        )}
      </div>
      <Card>
        <div className="grid gap-3 sm:grid-cols-5">
          <FormField label="From" name="fin-from">
            <Input
              id="fin-from"
              type="date"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
            />
          </FormField>
          <FormField label="To" name="fin-to">
            <Input id="fin-to" type="date" value={to} onChange={(e) => setTo(e.target.value)} />
          </FormField>
          <FormField label="Status" name="fin-status">
            <Select id="fin-status" value={status} onChange={(e) => setStatus(e.target.value)}>
              <option value="">All statuses</option>
              {(tab === 'payments'
                ? ['paid', 'partially_refunded', 'refunded', 'pending', 'failed']
                : ['pending', 'approved', 'processed', 'rejected']
              ).map((s) => (
                <option key={s} value={s}>
                  {STATUS_WORD(s)}
                </option>
              ))}
            </Select>
          </FormField>
          {tab === 'payments' && (
            <FormField label="Provider" name="fin-provider">
              <Select
                id="fin-provider"
                value={provider}
                onChange={(e) => setProvider(e.target.value)}
              >
                <option value="">All providers</option>
                <option value="bayarcash">Bayarcash</option>
                <option value="gift_certificate">Gift certificate</option>
                <option value="outside">Outside the app</option>
              </Select>
            </FormField>
          )}
          <FormField label="Search" name="fin-q">
            <Input
              id="fin-q"
              value={q}
              placeholder="Customer, email or booking"
              onChange={(e) => setQ(e.target.value)}
            />
          </FormField>
        </div>
      </Card>

      {list.isLoading && <Spinner />}
      {list.error && (
        <p className="text-sm text-semantic-danger-text">
          {(list.error as { message?: string }).message ?? 'The list could not be loaded.'}
        </p>
      )}
      {list.data?.truncated && (
        <p className="text-sm text-semantic-warning-text">
          More than 5,000 in this range: only the newest 5,000 are shown. Narrow the dates to see
          the rest.
        </p>
      )}

      {payments && (
        <Card>
          <div className="mb-3 flex items-center justify-between gap-3">
            <p data-testid="finance-totals" className="text-sm font-medium text-ink">
              {`${plural(payments.count, 'payment')} · Received ${rm(payments.totals.received_minor)} · Refunded ${rm(payments.totals.refunded_minor)} · Net ${rm(payments.totals.net_minor)}`}
            </p>
            <Button
              variant="secondary"
              onClick={() => download(`payments-${from}-${to}.csv`, paymentsToCsv(payments))}
            >
              Download CSV
            </Button>
          </div>
          <div className="overflow-x-auto">
            <table aria-label="Payments" className="min-w-full">
              <thead>
                <tr>
                  {[
                    'Date',
                    'Booking',
                    'Customer',
                    'Kind',
                    'Provider',
                    'Amount',
                    'Refunded',
                    'Status',
                  ].map((h) => (
                    <th key={h} className={TH}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {payments.items.map((p) => (
                  <tr key={p.id} className="border-t border-hairline-soft">
                    <td className={TD}>{when(p.date)}</td>
                    <td className={TD}>
                      <Link className="text-brand-blue underline" to={`/bookings/${p.booking_id}`}>
                        {shortRef(p.booking_id)}
                      </Link>
                    </td>
                    <td className={TD}>
                      {p.customer ? (
                        <>
                          <div>{p.customer.name}</div>
                          <div className="text-xs text-steel">{p.customer.email}</div>
                        </>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td className={TD}>{KIND_WORD[p.kind] ?? p.kind}</td>
                    <td className={TD}>
                      {PROVIDER_WORD[p.provider] ?? p.provider}
                      {p.channel && <div className="text-xs text-steel">{p.channel}</div>}
                    </td>
                    <td className={TD}>{rm(p.amount_minor)}</td>
                    <td className={TD}>{p.refunded_minor > 0 ? rm(p.refunded_minor) : '—'}</td>
                    <td className={TD}>
                      {STATUS_WORD(p.status)}
                      {p.mismatch && (
                        <div className="text-xs font-semibold text-semantic-warning-text">
                          Amount mismatch
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {refunds && (
        <Card>
          <div className="mb-3 flex items-center justify-between gap-3">
            <p data-testid="finance-totals" className="text-sm font-medium text-ink">
              {`${plural(refunds.count, 'refund')} · Refunded ${rm(refunds.totals.refunded_minor)}`}
            </p>
            <Button
              variant="secondary"
              onClick={() => download(`refunds-${from}-${to}.csv`, refundsToCsv(refunds))}
            >
              Download CSV
            </Button>
          </div>
          <div className="overflow-x-auto">
            <table aria-label="Refunds" className="min-w-full">
              <thead>
                <tr>
                  {[
                    'Logged',
                    'Booking',
                    'Customer',
                    'Amount',
                    'Status',
                    'Reason',
                    'Approved by',
                    'Processed',
                  ].map((h) => (
                    <th key={h} className={TH}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {refunds.items.map((r) => (
                  <tr key={r.id} className="border-t border-hairline-soft">
                    <td className={TD}>{when(r.created_at)}</td>
                    <td className={TD}>
                      <Link className="text-brand-blue underline" to={`/bookings/${r.booking_id}`}>
                        {shortRef(r.booking_id)}
                      </Link>
                    </td>
                    <td className={TD}>
                      {r.customer ? (
                        <>
                          <div>{r.customer.name}</div>
                          <div className="text-xs text-steel">{r.customer.email}</div>
                        </>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td className={TD}>{rm(r.amount_minor)}</td>
                    <td className={TD}>{STATUS_WORD(r.status)}</td>
                    <td className={TD}>{r.reason ?? '—'}</td>
                    <td className={TD}>{r.approved_by?.name ?? '—'}</td>
                    <td className={TD}>{when(r.processed_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}
